import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What services does Ursus Victor LLC provide?',
      answer: 'We provide professional loan application support and documentation services. This includes helping you complete application forms, organize required documents, review your application for accuracy, and guide you through the submission process. We do NOT lend money or make lending decisions.'
    },
    {
      question: 'Are you a lender?',
      answer: 'NO. Ursus Victor LLC is NOT a lender. We do not make loans, do not make lending decisions, and do not guarantee loan approval. We provide support services to help you prepare your loan application. All lending decisions are made solely by financial institutions.'
    },
    {
      question: 'Do you guarantee loan approval?',
      answer: 'No, we do NOT guarantee loan approval. We provide application support services to help you prepare the best possible application, but the final lending decision is made entirely by the financial institution you apply to. Approval depends on many factors including credit history, income, debt-to-income ratio, and lender-specific criteria.'
    },
    {
      question: 'What types of loans can you help with?',
      answer: 'We can assist with applications for personal loans, business loans, mortgages, auto loans, student loans, home equity loans, SBA loans, commercial real estate loans, equipment financing, lines of credit, construction loans, and refinancing. Our support services apply to most loan types.'
    },
    {
      question: 'How much do your services cost?',
      answer: 'Our fees vary depending on the complexity of your application and the level of support needed. Contact us for a free consultation where we will discuss your specific needs and provide transparent pricing information. We believe in upfront, honest pricing with no hidden fees.'
    },
    {
      question: 'How long does the process take?',
      answer: 'The timeline depends on several factors including the loan type, complexity of your situation, and how quickly you can provide required documents. Typically, we can help you prepare a complete application package within 5-10 business days. Rush service is available for time-sensitive applications.'
    },
    {
      question: 'What documents will I need?',
      answer: 'Required documents vary by loan type, but commonly include: government-issued ID, proof of income (pay stubs, tax returns), bank statements, credit reports, employment verification, and loan-specific documents. We provide a customized checklist based on your specific loan application.'
    },
    {
      question: 'Can you help if I have bad credit?',
      answer: 'Yes, we can help you prepare your application regardless of your credit situation. We can assist with organizing documentation that explains credit issues and help you present your application in the best possible light. However, we cannot guarantee approval, as lending decisions are made by financial institutions based on their criteria.'
    },
    {
      question: 'Do you work with specific lenders?',
      answer: 'No, we are independent and do not work exclusively with any particular lenders. Our goal is to help you prepare a complete, accurate application that you can submit to the lender of your choice. We can provide general guidance on lender requirements but do not make lender recommendations.'
    },
    {
      question: 'Is my information confidential?',
      answer: 'Yes, absolutely. We take confidentiality very seriously. Your personal and financial information is protected with strict security measures and will never be shared with third parties without your explicit consent. We comply with all applicable privacy laws and regulations.'
    },
    {
      question: 'Can you help with business loan applications?',
      answer: 'Yes, we provide comprehensive support for business loan applications including help with business plans, financial projections, business tax returns, profit & loss statements, balance sheets, and other business-specific documentation. We work with sole proprietors, partnerships, LLCs, and corporations.'
    },
    {
      question: 'What if the lender requests additional information?',
      answer: 'We provide ongoing support throughout the application process. If your lender requests additional documents or information, we can help you understand what is needed and assist with preparing the requested materials. Our support continues until your application is complete.'
    },
    {
      question: 'Do you offer virtual/remote services?',
      answer: 'Yes, all of our services are available remotely. We work with clients nationwide via email, phone, and secure document sharing platforms. You never need to visit an office - everything can be handled conveniently from your home or business.'
    },
    {
      question: 'Can you help with loan refinancing?',
      answer: 'Yes, we can assist with refinancing applications for mortgages, auto loans, student loans, and business loans. The process is similar to a new loan application, and we help you gather the necessary documentation and complete the refinancing application accurately.'
    },
    {
      question: 'What makes your service different?',
      answer: 'We specialize exclusively in loan application support, making us experts in this specific field. Unlike lenders who have a vested interest in the outcome, our only goal is to help you prepare the best possible application. We provide personalized, one-on-one support with meticulous attention to detail.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - Ursus Victor LLC | Loan Application Support Questions</title>
        <meta name="description" content="Frequently asked questions about our loan application support services. Learn how we can help with your loan documentation." />
      </Helmet>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        padding: '5rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3.5rem', fontWeight: '900', marginBottom: '1rem'}}>
            Frequently Asked Questions
          </h1>
          <p style={{fontSize: '1.3rem', opacity: 0.95, lineHeight: '1.6'}}>
            Everything you need to know about our loan application support services
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{padding: '5rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          <div style={{display: 'grid', gap: '1.5rem'}}>
            {faqs.map((faq, index) => (
              <div key={index} style={{
                background: '#f8fafc',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  style={{
                    width: '100%',
                    padding: '1.75rem',
                    background: openIndex === index ? '#dbeafe' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'background 0.3s'
                  }}
                >
                  <span style={{
                    fontSize: '1.2rem',
                    fontWeight: '800',
                    color: '#1e40af',
                    lineHeight: '1.5'
                  }}>
                    {faq.question}
                  </span>
                  <span style={{
                    fontSize: '1.5rem',
                    color: '#3b82f6',
                    flexShrink: 0,
                    fontWeight: '900'
                  }}>
                    {openIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openIndex === index && (
                  <div style={{
                    padding: '0 1.75rem 1.75rem',
                    fontSize: '1.05rem',
                    color: '#475569',
                    lineHeight: '1.8'
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section style={{padding: '5rem 2rem', background: '#f8fafc'}}>
        <div style={{maxWidth: '800px', margin: '0 auto', textAlign: 'center'}}>
          <h2 style={{fontSize: '2.5rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1.5rem'}}>
            Still Have Questions?
          </h2>
          <p style={{fontSize: '1.2rem', color: '#64748b', marginBottom: '2.5rem', lineHeight: '1.7'}}>
            We're here to help! Contact us for personalized answers to your specific questions.
          </p>
          <Link to="/contact" style={{
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            color: 'white',
            padding: '1.25rem 3rem',
            borderRadius: '12px',
            fontWeight: '800',
            fontSize: '1.2rem',
            display: 'inline-block',
            boxShadow: '0 8px 24px rgba(30,64,175,0.3)'
          }}>
            Contact Us
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section style={{
        background: '#fef3c7',
        padding: '2.5rem 2rem',
        borderTop: '3px solid #f59e0b'
      }}>
        <div style={{maxWidth: '1000px', margin: '0 auto', textAlign: 'center'}}>
          <p style={{fontSize: '1rem', color: '#92400e', lineHeight: '1.8', fontWeight: '600'}}>
            <strong>IMPORTANT DISCLOSURE:</strong> Ursus Victor LLC provides loan application support services only. 
            We are NOT a lender, do NOT make loans, and do NOT make lending decisions. We do NOT guarantee loan approval.
          </p>
        </div>
      </section>
    </>
  );
};

export default FAQ;
